import { keyframes } from 'styled-components'
import { transparentize } from 'polished'
import ms from 'modularscale'
import { generateMedia } from 'styled-media-query'
import reset from '!!raw-loader!app-reset'

// Global CSS styles. For reset purposes only.
export const globalStyle = `  
  @import url("https://use.typekit.net/goz6zxk.css");

  ${reset}

  html {
    -webkit-font-smoothing: antialiased;
  }

  html,
  body,
  #___gatsby {
    height: 100%;
  }

  // Disable gatsby-remark-images blur-up.
  .gatsby-resp-image-background-image {
    background-image: none !important;
  }
`

// Base values used to derive usable values. Property names should describe the
// actual value, not the use of the value.
const base = {
  color: {
    black: '#000',
    blue: '#00f',
    limeGreen: '#b5dba0',
    green: '#6fb175',
    lava: '#c84748',
    magenta: '#f0f',
    peach: '#fdc19a',
    tealDark: '#254554',
    white: '#fff',
    yellowLight: '#ffd98f',
    coolBlue: '#c9e9ea',
  },
  fontFamily: {
    interstateCompressed: 'interstate-compressed',
  },
  fontWeight: {
    thin: 100,
    extraLight: 200,
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  mediaSizes: {
    largeDesktop: 1170,
    desktop: 992,
    tablet: 768,
    phone: 376,
  },
}

// Theme values. Utilize the base values as much as possible. Property names
// should be declarative and describe the use of the value.
export const theme = {
  animation: {
    upAndDown: keyframes`
      0%,
      100% {
        transform: translateY(-30%);
      }

      50% {
        transform: translateY(0%);
      }
    `,
    sideToSide: keyframes`
      0%,
      100% {
        transform: translateX(-2.5%);
      }

      50% {
        transform: translateX(2.5%);
      }
    `,
    explodingStroke: keyframes`
      99% {
        pointer-events: none;
        stroke-width: 10vmin;
        stroke: transparent;
      }

      100% {
        pointer-events: auto;
        stroke-width: 0;
      }
    `,
    swingInHorizontal: keyframes`
      from{
        opacity: 0;
        transform: rotatey(25deg);
      }
      to{
        opacity: 1;
        transform: rotatey(0deg);
      }
    `,
    swingInVertical: keyframes`
      from{
        opacity: 0;
        transform: rotatex(-25deg);
      }
      to{
        opacity: 1;
        transform: rotatex(0deg);
      }
    `,
    temporaryFade: keyframes`
      0% {
        opacity: 0;
      }
      10% {
        opacity: 0.3;
      }
      80% {
        opacity: 0.3;
      }
      100% {
        opacity: 0;
      }
    `,
  },
  base,
  color: {
    ...base.color,

    /***
     * General
     */
    copy: base.color.white,
    copySecondary: base.color.yellowLight,
    accent: base.color.peach,

    /***
     * Component-specific
     */
    // Button
    Button: {
      bg: ['#fdd387', '#eda147'],
      bgHover: ['#eda14c', '#e9813d'],
      bgActive: ['#b2d899', '#71b276'],
      bgDisabled: ['#abbec1', '#708083'],
      shadowInset: '#eda44c',
      shadowInsetDisabled: '#6c7d81',
      shadowInsetHover: '#e9813d',
      shadowInsetActive: '#71b276',
      copy: '#fefbd8',
      copyDisabled: '#b2c3c8',
      border: '#fefad8',
    },

    // FooterHeader
    FooterHeader: {
      bg: ['#3ea9b4', '#264554'],
      heading: ['#eda44c', '#ffd990'],
    },

    // Hint
    Hint: {
      bg: ['#ab283e', '#eb725f'],
      copy: base.color.white,
      heading: base.color.peach,
    },

    // IrregularInteraction
    IrregularInteraction: {
      highlightPending: base.color.lava,
      highlightSuccess: base.color.green,
    },

    // Status
    Status: {
      barBg: ['#3b8d63', '#19352a'],
    },

    // Tip
    Tip: {
      bg: [transparentize(0.1, '#2a8d95'), transparentize(0.05, '#032e39')],
      copySecondary: '#ffdf90',
    },

    /***
     * Page-specific
     */
    // living-room
    LivingRoomPage: {
      bg: ['#ffd98f', '#6fb175', '#059ca6'],
    },
  },

  fontFamily: {
    primary: `${base.fontFamily.interstateCompressed}, sans-serif`,
  },

  fontSize: (step) => ms(step, 'augmented fourth') + 'rem',

  fontWeight: {
    regular: base.fontWeight.medium,
  },

  radius: (step) => ms(step, 'augmented fourth') + 'rem',

  spacing: (step) => ms(step, 'augmented fourth') + 'rem',

  mediaSizes: {
    // Mobile versions
    small: base.mediaSizes.phone,
    medium: base.mediaSizes.tablet,

    // Desktop versions
    desktop: base.mediaSizes.desktop,
    large: base.mediaSizes.desktop,
    extraLarge: base.mediaSizes.largeDesktop,
  },

  transition: '250ms',
}

// Add px suffix to media sizes.
const mediaSizesWithPx = Object.keys(theme.mediaSizes).reduce((acc, key) => {
  const val = theme.mediaSizes[key]
  acc[key] = val + 'px'
  return acc
}, {})

// Media query helper.
export const media = generateMedia(mediaSizesWithPx)



// WEBPACK FOOTER //
// ./src/theme.js